import { Board, BoardColumn } from '../../lib/boards'

export const overlayClass = 'fixed inset-0 bg-gray-950/60 flex items-center justify-center p-4 animate-fadeIn'

export const panelClass = 'bg-white dark:bg-surface-100 rounded-2xl shadow-2xl ring-1 ring-black/5 dark:ring-white/10 animate-modalIn'

export const primaryButton =
  'inline-flex items-center justify-center gap-1.5 h-9 px-4 rounded-lg bg-brand-600 text-white text-sm font-medium shadow-sm hover:bg-brand-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors'

export const secondaryButton =
  'inline-flex items-center justify-center gap-1.5 h-9 px-4 rounded-lg border border-gray-200 dark:border-white/10 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-white/5 disabled:opacity-40 disabled:cursor-not-allowed transition-colors'

export const ghostButton =
  'inline-flex items-center justify-center gap-1.5 h-9 px-4 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/5 transition-colors'

export const iconButton =
  'p-1.5 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/5 disabled:opacity-30 disabled:pointer-events-none transition-colors'

export const sectionTitleClass = 'text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400'

type Accent = { dot: string; soft: string }
type AccentName = 'brand' | 'sky' | 'orange' | 'violet' | 'indigo' | 'teal' | 'green' | 'amber' | 'gray' | 'slate'

const ACCENTS: Record<AccentName, Accent> = {
  brand: { dot: 'bg-brand-400', soft: 'bg-brand-400/10 text-brand-600 dark:text-brand-400' },
  sky: { dot: 'bg-sky-500', soft: 'bg-sky-500/10 text-sky-700 dark:text-sky-300' },
  orange: { dot: 'bg-orange-500', soft: 'bg-orange-500/10 text-orange-700 dark:text-orange-300' },
  violet: { dot: 'bg-violet-500', soft: 'bg-violet-500/10 text-violet-700 dark:text-violet-300' },
  indigo: { dot: 'bg-indigo-500', soft: 'bg-indigo-500/10 text-indigo-700 dark:text-indigo-300' },
  teal: { dot: 'bg-teal-500', soft: 'bg-teal-500/10 text-teal-700 dark:text-teal-300' },
  green: { dot: 'bg-green-500', soft: 'bg-green-500/10 text-green-700 dark:text-green-300' },
  amber: { dot: 'bg-amber-500', soft: 'bg-amber-500/10 text-amber-700 dark:text-amber-300' },
  gray: { dot: 'bg-gray-400', soft: 'bg-gray-500/10 text-gray-600 dark:text-gray-300' },
  slate: { dot: 'bg-slate-600 dark:bg-slate-500', soft: 'bg-slate-500/10 text-slate-700 dark:text-slate-300' },
}

const KEY_ACCENT: Record<string, AccentName> = {
  new_enquiry: 'sky',
  no_answer: 'orange',
  first_call_done: 'violet',
  finding_tutor: 'indigo',
  tutor_matched: 'teal',
  active: 'green',
  payment_due: 'amber',
  paused: 'gray',
  closed: 'slate',
}

const KIND_ACCENT: Record<BoardColumn['kind'], AccentName> = { stage: 'brand', auto: 'green', parked: 'gray' }

export function columnAccent(column: Pick<BoardColumn, 'key' | 'kind'>): Accent {
  return ACCENTS[KEY_ACCENT[column.key] ?? KIND_ACCENT[column.kind]]
}

export function Avatar({ name, size = 'sm' }: { name: string; size?: 'sm' | 'md' }) {
  const initials =
    name
      .split(/[\s@._-]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map(w => w[0])
      .join('')
      .toUpperCase() || '?'
  return (
    <span
      className={`${size === 'sm' ? 'h-5 w-5 text-[10px]' : 'h-6 w-6 text-[11px]'} flex-shrink-0 rounded-full bg-brand-400/15 text-brand-600 dark:text-brand-400 font-semibold flex items-center justify-center`}
    >
      {initials}
    </span>
  )
}

export function BoardTabs({ boards, activeId, onSelect }: { boards: Board[]; activeId: string | undefined; onSelect: (board: Board) => void }) {
  return (
    <div className="inline-flex gap-1 p-1 rounded-xl bg-gray-100 dark:bg-white/5" role="tablist">
      {boards.map(b => (
        <button
          key={b.id}
          role="tab"
          aria-selected={b.id === activeId}
          onClick={() => onSelect(b)}
          className={`px-4 h-8 text-sm font-medium rounded-lg transition-colors ${
            b.id === activeId
              ? 'bg-white dark:bg-surface-100 text-gray-900 dark:text-white shadow-sm'
              : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
          }`}
        >
          {b.name}
        </button>
      ))}
    </div>
  )
}

export function Switch({ checked, onChange, label, disabled = false }: {
  checked: boolean; onChange: (checked: boolean) => void; label: string; disabled?: boolean
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/50 disabled:opacity-50 ${
        checked ? 'bg-brand-600' : 'bg-gray-200 dark:bg-white/15'
      }`}
    >
      <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-[18px]' : 'translate-x-0.5'}`} />
    </button>
  )
}
